const ACCESS_DENIED_KEY = "prescriberAccessDenied";
const ACCESS_DENIED_SHOWN_KEY = "prescriberAccessDeniedShown";

/**
 * Stores a prescriber access-denied reason once, until the login page shows it.
 * @param {string} reason - USER_DISABLED, OWNER_DISABLED, or NO_COMPANY.
 * @returns {void}
 */
export const rememberPrescriberAccessDenied = (reason) => {
  if (!reason || sessionStorage.getItem(ACCESS_DENIED_SHOWN_KEY)) {
    return;
  }
  sessionStorage.setItem(ACCESS_DENIED_KEY, reason);
};

/**
 * Reads and consumes a pending access-denied reason.
 * Returns null if the message was already shown (so reload goes to login).
 * @returns {string|null} USER_DISABLED, OWNER_DISABLED, NO_COMPANY, or null.
 */
export const consumePrescriberAccessDenied = () => {
  const reason = sessionStorage.getItem(ACCESS_DENIED_KEY);
  const alreadyShown = sessionStorage.getItem(ACCESS_DENIED_SHOWN_KEY);
  sessionStorage.removeItem(ACCESS_DENIED_KEY);

  if (alreadyShown) {
    sessionStorage.removeItem(ACCESS_DENIED_SHOWN_KEY);
    return null;
  }

  if (!reason) {
    return null;
  }

  sessionStorage.setItem(ACCESS_DENIED_SHOWN_KEY, "1");
  if (reason === "NO_COMPANY") return "NO_COMPANY";
  if (reason === "OWNER_DISABLED") return "OWNER_DISABLED";
  return "USER_DISABLED";
};

/**
 * Clears any stored prescriber access-denied state.
 * @returns {void}
 */
export const clearPrescriberAccessDenied = () => {
  sessionStorage.removeItem(ACCESS_DENIED_KEY);
  sessionStorage.removeItem(ACCESS_DENIED_SHOWN_KEY);
};

/**
 * Returns whether the user is a practice OWNER.
 * @param {object} user - Authenticated user with role.
 * @returns {boolean}
 */
export const isPracticeOwner = (user) =>
  Boolean(user && user.role && user.role.role === "OWNER");

/**
 * Returns whether the user is a practice MANAGER.
 * @param {object} user - Authenticated user with role.
 * @returns {boolean}
 */
export const isPracticeManager = (user) =>
  Boolean(user && user.role && user.role.role === "MANAGER");

/**
 * Returns whether the user can manage the practice (owner or manager).
 * @param {object} user - Authenticated user with role.
 * @returns {boolean}
 */
export const isPracticeAdmin = (user) =>
  isPracticeOwner(user) || isPracticeManager(user);

/**
 * Returns whether the user is a backoffice admin (role or is_admin flag).
 * Prescribing roles are never treated as backoffice admins, even if is_admin is set.
 * @param {object} user - Authenticated user with role.
 * @returns {boolean}
 */
export const isBackofficeAdmin = (user) => {
  if (!user) {
    return false;
  }
  const role = user.role && user.role.role;
  if (role === "OWNER" || role === "MANAGER" || role === "PRESCRIBER") {
    return false;
  }
  return Boolean(user.is_admin || role === "ADMIN");
};

/**
 * Normalizes a team API payload into an array of members.
 * @param {object|Array} payload - API response body.
 * @returns {Array<object>} Team members.
 */
export const parseTeamMembers = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};
